/**
 * Reframes the scenes a speaker→face change affects (PLAT-11992): every
 * scene led by one of `speakerLabels` — or every scene, in a two-person video,
 * where each speaker is framed by ruling out the other.
 *
 * Plans only: returns each scene's new layout for the caller to commit in one
 * command, so a single undo reverts the change. Scenes led by anyone else keep
 * their framing, manual crops included.
 */

import { deepClone } from '@src/utility/deepClone';
import { LogManager } from '@src/utility/Logger';

import type { SmartFramingService } from './SmartFramingService';
import type { FaceDetection } from './types';
import type { Scene } from '@src/editor/models/layout/Scene';
import type { IScene } from '@src/editor/models/layout/types';

export type SceneReframer = Pick<
  SmartFramingService,
  | 'computeSpeakerContextFraming'
  | 'applyCropBoxesToScene'
  | 'getSceneInfos'
  | 'timelineSpeakers'
>;

const logger = LogManager.getLogger('service:planSceneReframe');

/** Crops this close count as the same framing (normalised units). */
const CROP_EPSILON = 0.001;

export function planSceneReframe({
  framer,
  faceDetections,
  speakerMap,
  speakerLabels,
  targetAspectRatio,
  onlyListedSpeakers = false,
  excludeSceneIds,
  fallbackOnlyFrom,
  autoFramedOnlyFrom,
}: {
  framer: SceneReframer;
  faceDetections: FaceDetection[];
  speakerMap: Record<string, string[]>;
  speakerLabels: ReadonlySet<string>;
  targetAspectRatio?: number;
  /** Never widen to the other speaker's scenes in a two-person video. */
  onlyListedSpeakers?: boolean;
  excludeSceneIds?: ReadonlySet<string>;
  /**
   * The map before the change. When given, only scenes that *fell back*
   * under it are reframed: auto-framing didn't land on their speaker, and
   * their crop is still the one auto-framing gave them (not hand-framed).
   */
  fallbackOnlyFrom?: Record<string, string[]>;
  /**
   * The map and face data before the change (PLAT-12144). When given, only
   * scenes whose crop is still the one auto-framing gave them under it are
   * reframed — hand-framed scenes are kept — and only when the crop moves.
   * Unlike `fallbackOnlyFrom`, a scene auto-framed on the wrong face is
   * reframed too, so correcting a mapping fixes it.
   */
  autoFramedOnlyFrom?: {
    speakerMap: Record<string, string[]>;
    faceDetections: FaceDetection[];
  };
}): Array<{ sceneId: string; updates: Partial<IScene> }> {
  const isTwoPerson =
    !onlyListedSpeakers && framer.timelineSpeakers().length === 2;
  const sceneUpdates: Array<{ sceneId: string; updates: Partial<IScene> }> = [];

  // One entry per scene saying what was decided and why, logged once at the
  // end — enough to replay why a scene was or wasn't reframed.
  const decisions: Array<Record<string, unknown>> = [];

  for (const sceneInfo of framer.getSceneInfos()) {
    const sceneId = sceneInfo.scene.id;
    const decide = (decision: string, details: Record<string, unknown> = {}) =>
      decisions.push({
        sceneId,
        start: Number(sceneInfo.startTime.toFixed(2)),
        end: Number(sceneInfo.endTime.toFixed(2)),
        decision,
        ...details,
      });

    if (excludeSceneIds?.has(sceneId)) {
      decide('skip: the scene being framed');
      continue;
    }
    const framing = framer.computeSpeakerContextFraming(
      sceneInfo,
      faceDetections,
      speakerMap,
      targetAspectRatio,
    );
    if (!framing?.topSpeaker) {
      decide('skip: nobody speaks or no faces', { framed: !!framing });
      continue;
    }
    if (!isTwoPerson && !speakerLabels.has(framing.topSpeaker)) {
      decide("skip: another speaker's scene", {
        topSpeaker: framing.topSpeaker,
      });
      continue;
    }

    if (fallbackOnlyFrom) {
      const before = framer.computeSpeakerContextFraming(
        sceneInfo,
        faceDetections,
        fallbackOnlyFrom,
        targetAspectRatio,
      );
      if (!before) {
        decide('skip: could not compute the previous framing', {
          topSpeaker: framing.topSpeaker,
        });
        continue;
      }
      if (before.speakerLabel === framing.topSpeaker) {
        decide('skip: already framed on the speaker', {
          topSpeaker: framing.topSpeaker,
          previousFaces: before.faceLabels,
        });
        continue;
      }
      const current = currentCrop(sceneInfo.scene);
      if (!cropsMatch(current, before.cropBoxes[0])) {
        decide('skip: framed by hand (crop differs from auto-framing)', {
          topSpeaker: framing.topSpeaker,
          currentCrop: current,
          autoCrop: before.cropBoxes[0],
          previousFaces: before.faceLabels,
        });
        continue;
      }
    }

    if (autoFramedOnlyFrom) {
      const before = framer.computeSpeakerContextFraming(
        sceneInfo,
        autoFramedOnlyFrom.faceDetections,
        autoFramedOnlyFrom.speakerMap,
        targetAspectRatio,
      );
      const current = currentCrop(sceneInfo.scene);
      if (!before || !cropsMatch(current, before.cropBoxes[0])) {
        decide('skip: framed by hand (crop differs from auto-framing)', {
          topSpeaker: framing.topSpeaker,
          currentCrop: current,
          autoCrop: before?.cropBoxes[0],
        });
        continue;
      }
      if (cropsMatch(current, framing.cropBoxes[0])) {
        decide('skip: framing unchanged', { topSpeaker: framing.topSpeaker });
        continue;
      }
    }

    const layout = deepClone(sceneInfo.scene.layout.toJSON());
    framer.applyCropBoxesToScene(framing.cropBoxes, { layout } as IScene);
    sceneUpdates.push({ sceneId, updates: { layout } });
    decide('reframe', {
      topSpeaker: framing.topSpeaker,
      faces: framing.faceLabels,
      framedSpeaker: framing.speakerLabel,
      crop: framing.cropBoxes[0],
    });
  }

  logger.info('Planned scene reframe', {
    speakerLabels: [...speakerLabels],
    isTwoPerson,
    fallbackOnly: !!fallbackOnlyFrom,
    autoFramedOnly: !!autoFramedOnlyFrom,
    reframed: sceneUpdates.length,
  });
  logger.debug('Scene reframe decisions', { decisions });

  return sceneUpdates;
}

/** The crop on the scene's first binding — the slot auto-framing writes. */
function currentCrop(
  scene: Scene,
): { x: number; y: number; w: number; h: number } | undefined {
  const [binding] = Object.values(scene.layout.toJSON().bindings ?? {});
  return binding?.transformKeyframes?.[0]?.crop;
}

function cropsMatch(
  current: { x: number; y: number; w: number; h: number } | undefined,
  crop: { x: number; y: number; width: number; height: number } | undefined,
): boolean {
  if (!crop || !current) return false;
  return (
    Math.abs(current.x - crop.x) < CROP_EPSILON &&
    Math.abs(current.y - crop.y) < CROP_EPSILON &&
    Math.abs(current.w - crop.width) < CROP_EPSILON &&
    Math.abs(current.h - crop.height) < CROP_EPSILON
  );
}
